<template>
  <section class="dash-panel dash-chat-panel" v-if="visible">
    <div class="dash-panel__header">
      <div>
        <span class="dash-panel__eyebrow">聊天记录</span>
        <h2>{{ parentName }} · 沟通详情</h2>
      </div>
      <div class="dash-meta">
        <span>共 {{ totalRecords || records.length }} 条消息</span>
        <button type="button" class="dash-chat-close" @click="closePanel">收起</button>
      </div>
    </div>

    <p v-if="targetFound === false" class="dash-chat-target-status">
      未找到该来源消息，已显示当前聊天记录。
    </p>

    <div class="dash-chat-body" v-if="records.length">
      <template v-for="(msg, idx) in records" :key="idx">
        <div v-if="showDateDivider(idx)" class="dash-chat-date">
          <span>{{ formatDate(msg.msgtime) }}</span>
        </div>
        <div
          :ref="(element) => setMessageElement(msg.msgid, element)"
          :data-msgid="msg.msgid"
          :class="['dash-chat-msg', isSelf(msg) ? 'is-self' : '', isHighlighted(msg) ? 'is-target' : '']"
        >
          <div class="dash-chat-avatar">{{ isSelf(msg) ? '顾' : '客' }}</div>
          <div class="dash-chat-content">
            <div class="dash-chat-name">{{ isSelf(msg) ? '顾问' : '客户' }}</div>
            <div class="dash-chat-bubble">
              <template v-if="msg.msgtype === 'text'">{{ msg.content }}</template>
              <template v-else-if="msg.msgtype === 'image' || msg.msgtype === 'img'">
                <button
                  v-if="mediaSrc(msg)"
                  type="button"
                  class="dash-chat-img-btn"
                  aria-label="预览图片"
                  @click="previewImg(mediaSrc(msg))"
                >
                  <img
                    :src="mediaSrc(msg)"
                    class="dash-chat-img"
                    alt="聊天图片"
                  />
                </button>
                <span v-else class="dash-chat-media-fallback">[图片]</span>
              </template>
              <template v-else-if="msg.msgtype === 'voice'">
                <div class="dash-chat-voice">
                  <span class="dash-chat-voice-icon">🎤</span> 语音消息
                </div>
                <audio
                  v-if="mediaSrc(msg)"
                  controls
                  preload="metadata"
                  :src="mediaSrc(msg)"
                  class="dash-chat-audio"
                  @play="pauseOthers"
                ></audio>
              </template>
              <template v-else-if="msg.msgtype === 'video'">
                <div v-if="mediaSrc(msg)" class="dash-chat-video-card">
                  <video
                    controls
                    preload="metadata"
                    :src="mediaSrc(msg)"
                    class="dash-chat-video"
                    @play="pauseOthers"
                  ></video>
                  <button type="button" class="dash-chat-video-open" @click="previewVideo(mediaSrc(msg))">放大查看</button>
                </div>
                <span v-else class="dash-chat-media-fallback">[视频]</span>
              </template>
              <template v-else-if="msg.msgtype === 'av_call'">
                <span class="dash-chat-call">📞 {{ msg.content || '语音通话' }}</span>
              </template>
              <template v-else-if="msg.msgtype === 'audio_archive'">
                <div class="dash-chat-archive">🎙️ 通话录音</div>
                <audio
                  v-if="mediaSrc(msg)"
                  controls
                  preload="metadata"
                  :src="mediaSrc(msg)"
                  class="dash-chat-audio"
                  @play="pauseOthers"
                ></audio>
              </template>
              <template v-else-if="msg.msgtype === 'file'">
                <a
                  v-if="mediaSrc(msg)"
                  :href="mediaSrc(msg)"
                  target="_blank"
                  rel="noopener"
                  class="dash-chat-file"
                >
                  <span class="dash-chat-file-icon">📁</span>
                  <div class="dash-chat-file-info">
                    <span class="dash-chat-file-name">查看文件</span>
                  </div>
                </a>
                <span v-else class="dash-chat-media-fallback">[文件]</span>
              </template>
              <template v-else>{{ msg.content || '[' + msg.msgtype + ']' }}</template>
            </div>
            <div class="dash-chat-time">{{ formatTime(msg.msgtime) }}</div>
          </div>
        </div>
      </template>

      <div v-if="hasMore" class="dash-chat-loadmore">
        <button type="button" class="dash-chat-loadmore-btn" @click="loadMore" :disabled="loading">
          {{ loading ? '加载中...' : '加载更多' }}
        </button>
      </div>
    </div>

    <div v-else-if="loading" class="dash-chat-empty">加载中...</div>
    <div v-else class="dash-chat-empty">暂无聊天记录</div>

    <div
      v-if="previewUrl"
      class="dash-chat-overlay"
      @click="previewUrl = ''"
    >
      <img :src="previewUrl" alt="图片预览" />
    </div>

    <div
      v-if="previewVideoUrl"
      class="dash-chat-overlay"
      @click="previewVideoUrl = ''"
    >
      <video :src="previewVideoUrl" controls autoplay class="dash-chat-overlay-video" @click.stop></video>
    </div>
  </section>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { getChatLogs } from '@/api'

const props = defineProps({
  campPeriodName: { type: String, default: '' },
  customerId: { type: String, default: '' },
  saleId: { type: String, default: '' },
  parentName: { type: String, default: '' },
  visible: { type: Boolean, default: false },
  selfCheckField: { type: String, default: 'teacher' },
  targetMsgid: { type: String, default: '' },
})

const emit = defineEmits(['close'])

const records = ref([])
const loading = ref(false)
const previewUrl = ref('')
const previewVideoUrl = ref('')
const currentPage = ref(1)
const pageSize = ref(50)
const totalRecords = ref(0)
const hasMore = ref(false)
const targetFound = ref(null)
const highlightedMsgid = ref('')
const messageElements = new Map()
let highlightTimer
let requestSequence = 0

function isSelf(msg) {
  return props.selfCheckField === 'teacher'
    ? msg.sender === 'teacher'
    : msg.sender === props.saleId
}

function cleanUrl(str) {
  return (str || '').trim().replace(/^`+|`+$/g, '')
}

function mediaSrc(msg) {
  return cleanUrl(msg.mediaUrl) || (msg.content && /^https?:\/\//.test(msg.content) ? cleanUrl(msg.content) : '')
}

function pauseOthers(e) {
  const panel = e.target.closest('.dash-chat-panel')
  if (!panel) return
  panel.querySelectorAll('audio, video').forEach((el) => {
    if (el !== e.target) {
      el.pause()
    }
  })
}

function showDateDivider(idx) {
  if (idx === 0) return true
  return formatDate(records.value[idx].msgtime) !== formatDate(records.value[idx - 1].msgtime)
}

function parseDate(str) {
  if (!str) return null
  let d = new Date(str)
  if (!isNaN(d.getTime())) return d
  const m = str.match(/^(\w{3})\s+(\w{3})\s+(\d{1,2})\s+(\d{2}):(\d{2}):(\d{2})\s+\w+\s+(\d{4})$/)
  if (m) {
    const months = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 }
    d = new Date(+m[7], months[m[2]] || 0, +m[3], +m[4], +m[5], +m[6])
    if (!isNaN(d.getTime())) return d
  }
  return null
}

function formatDate(str) {
  const d = parseDate(str)
  if (!d) return str ? str.slice(0, 10) : ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatTime(str) {
  const d = parseDate(str)
  if (!d) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function previewImg(url) {
  previewUrl.value = url
}

function previewVideo(url) {
  previewVideoUrl.value = url
}

function messageId(msgid) {
  return msgid == null ? '' : String(msgid)
}

function setMessageElement(msgid, element) {
  const id = messageId(msgid)
  if (!id) return
  if (element) {
    messageElements.set(id, element)
  } else {
    messageElements.delete(id)
  }
}

function isHighlighted(msg) {
  return messageId(msg.msgid) === highlightedMsgid.value
}

function clearTargetState() {
  targetFound.value = null
  highlightedMsgid.value = ''
  clearTimeout(highlightTimer)
}

function closePanel() {
  invalidateRequests()
  clearTargetState()
  emit('close')
}

function invalidateRequests() {
  requestSequence++
  loading.value = false
}

function isCurrentRequest(sequence, customerId, saleId, targetMsgid) {
  return sequence === requestSequence
    && props.visible
    && props.customerId === customerId
    && props.saleId === saleId
    && props.targetMsgid === targetMsgid
}

async function highlightTarget(targetMsgid, isCurrent) {
  highlightedMsgid.value = targetMsgid
  await nextTick()
  if (!isCurrent()) return
  messageElements.get(targetMsgid)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  clearTimeout(highlightTimer)
  highlightTimer = setTimeout(() => {
    if (highlightedMsgid.value === targetMsgid) highlightedMsgid.value = ''
  }, 2000)
}

async function fetchChatLogs(append = false) {
  if (!props.customerId || !props.saleId) return
  const sequence = requestSequence
  const customerId = props.customerId
  const saleId = props.saleId
  const targetMsgid = props.targetMsgid
  const requestedTargetMsgid = append ? '' : targetMsgid
  const isCurrent = () => isCurrentRequest(sequence, customerId, saleId, targetMsgid)
  loading.value = true
  try {
    const params = {
      customerId,
      saleId,
      campPeriodName: props.campPeriodName,
      page: currentPage.value,
      size: pageSize.value,
    }
    if (requestedTargetMsgid) params.targetMsgid = requestedTargetMsgid
    const res = await getChatLogs(params)
    if (!isCurrent()) return
    const data = res.code === 200 ? res.data : res
    let list = []
    if (Array.isArray(data)) {
      list = data
    } else if (data && Array.isArray(data.messages)) {
      list = data.messages
      totalRecords.value = data.total || list.length
    }
    if (Array.isArray(data)) totalRecords.value = list.length
    if (append) {
      records.value = [...records.value, ...list]
    } else {
      records.value = list
      if (requestedTargetMsgid && data && !Array.isArray(data)) {
        targetFound.value = data.targetFound === false ? false : null
        if (data.targetFound === true && list.some((msg) => messageId(msg.msgid) === requestedTargetMsgid)) {
          await highlightTarget(requestedTargetMsgid, isCurrent)
          if (!isCurrent()) return
        }
      }
    }
    hasMore.value = records.value.length < totalRecords.value
  } catch (err) {
    if (!isCurrent()) return
    console.error('获取聊天记录失败', err)
    if (!append) records.value = []
  } finally {
    if (isCurrent()) loading.value = false
  }
}

function loadMore() {
  currentPage.value++
  fetchChatLogs(true)
}

watch(
  () => [props.campPeriodName, props.customerId, props.saleId, props.visible, props.targetMsgid],
  () => {
    invalidateRequests()
    if (props.visible && props.customerId && props.saleId) {
      clearTargetState()
      currentPage.value = 1
      records.value = []
      fetchChatLogs()
    } else {
      clearTargetState()
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  invalidateRequests()
  clearTimeout(highlightTimer)
})
</script>

<style scoped>
.dash-chat-panel {
  margin-top: 10px;
  min-height: auto;
}

.dash-chat-close {
  padding: 4px 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: transparent;
  color: var(--ink-soft);
  font-size: 0.72rem;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;
}

.dash-chat-close:hover {
  background: rgba(41, 107, 115, 0.08);
  color: var(--brand);
}

.dash-chat-body {
  margin-top: 12px;
  max-height: 600px;
  overflow-y: auto;
  padding: 0 4px;
  overscroll-behavior-y: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.dash-chat-body::-webkit-scrollbar {
  display: none;
}

.dash-chat-target-status {
  margin: 10px 4px 0;
  color: #8a5b18;
  font-size: 0.76rem;
}

.dash-chat-date {
  text-align: center;
  margin: 16px 0 10px;
}

.dash-chat-date span {
  display: inline-block;
  padding: 3px 14px;
  border-radius: 999px;
  background: rgba(29, 47, 44, 0.06);
  color: var(--ink-soft);
  font-size: 0.7rem;
}

.dash-chat-msg {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.dash-chat-msg.is-self {
  flex-direction: row-reverse;
}

.dash-chat-msg.is-target {
  border-radius: 8px;
  box-shadow: 0 0 0 2px rgba(211, 154, 53, 0.5);
  animation: dash-chat-target-pulse 700ms ease-in-out 2;
}

@keyframes dash-chat-target-pulse {
  50% {
    background: rgba(211, 154, 53, 0.12);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dash-chat-msg.is-target {
    animation: none;
  }
}

.dash-chat-avatar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
  background: var(--brand);
}

.dash-chat-msg:not(.is-self) .dash-chat-avatar {
  background: #4a90d9;
}

.dash-chat-content {
  max-width: min(68%, 720px);
}

.dash-chat-msg.is-self .dash-chat-content {
  text-align: right;
}

.dash-chat-name {
  font-size: 0.68rem;
  color: var(--ink-soft);
  margin-bottom: 3px;
}


.dash-chat-bubble {
  display: inline-block;
  padding: 9px 13px;
  border-radius: 12px;
  font-size: 0.84rem;
  line-height: 1.55;
  word-break: break-word;
  white-space: pre-wrap;
  text-align: left;
  background: rgba(255, 250, 244, 0.92);
  border: 1px solid rgba(29, 47, 44, 0.06);
  border-top-left-radius: 2px;
}

.dash-chat-msg.is-self .dash-chat-bubble {
  background: rgba(149, 236, 105, 0.28);
  border-color: rgba(43, 139, 100, 0.12);
  border-top-left-radius: 12px;
  border-top-right-radius: 2px;
}

.dash-chat-time {
  margin-top: 2px;
  font-size: 0.64rem;
  color: rgba(29, 47, 44, 0.35);
}

.dash-chat-img-btn {
  display: inline-block;
  max-width: 240px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.dash-chat-img {
  display: block;
  width: 100%;
  max-width: 240px;
  border-radius: 6px;
}

.dash-chat-video-card {
  position: relative;
  width: min(420px, 58vw);
  max-width: 100%;
}

.dash-chat-video {
  display: block;
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  border-radius: 10px;
  background: rgba(29, 47, 44, 0.08);
}

.dash-chat-video-open {
  position: absolute;
  top: 8px;
  right: 8px;
  border: 0;
  border-radius: 999px;
  padding: 5px 10px;
  background: rgba(29, 47, 44, 0.68);
  color: #fff;
  font-size: 0.7rem;
  cursor: pointer;
}

.dash-chat-audio {
  display: block;
  width: 100%;
  max-width: 280px;
  margin-top: 6px;
  height: 36px;
}

.dash-chat-voice {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--ink-soft);
  font-size: 0.78rem;
  margin-bottom: 4px;
}

.dash-chat-voice-icon {
  font-size: 1rem;
}

.dash-chat-call {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--good);
  font-weight: 500;
  font-size: 0.84rem;
}

.dash-chat-archive {
  color: var(--ink-soft);
  font-size: 0.78rem;
  margin-bottom: 4px;
}

.dash-chat-file {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(29, 47, 44, 0.04);
  text-decoration: none;
  color: var(--ink);
  transition: background 140ms ease;
}

.dash-chat-file:hover {
  background: rgba(29, 47, 44, 0.08);
}

.dash-chat-file-icon {
  font-size: 1.4rem;
  flex-shrink: 0;
}

.dash-chat-file-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dash-chat-file-name {
  font-size: 0.78rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dash-chat-file-size {
  font-size: 0.68rem;
  color: var(--ink-soft);
  margin-top: 2px;
}

.dash-chat-media-fallback {
  color: var(--ink-soft);
  font-size: 0.78rem;
}

.dash-chat-empty {
  text-align: center;
  padding: 40px 20px;
  color: var(--ink-soft);
  font-size: 0.84rem;
}

.dash-chat-loadmore {
  text-align: center;
  padding: 16px 0 8px;
}

.dash-chat-loadmore-btn {
  padding: 6px 20px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: transparent;
  color: var(--brand);
  font-size: 0.76rem;
  cursor: pointer;
  transition: background 140ms ease;
}

.dash-chat-loadmore-btn:hover:not(:disabled) {
  background: rgba(41, 107, 115, 0.06);
}

.dash-chat-loadmore-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dash-chat-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.82);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.dash-chat-overlay img {
  max-width: 90%;
  max-height: 90%;
  border-radius: 10px;
}

.dash-chat-overlay-video {
  max-width: min(92vw, 980px);
  max-height: 90vh;
  border-radius: 12px;
  background: #000;
}

@media (max-width: 860px) {
  .dash-chat-content {
    max-width: 80%;
  }

  .dash-chat-body {
    max-height: none;
  }

  .dash-chat-img-btn,
  .dash-chat-img {
    max-width: 180px;
  }

  .dash-chat-video-card {
    width: min(280px, 78vw);
  }

  .dash-chat-video {
    max-height: 260px;
  }
}
</style>
